import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { facturationService } from '@/services/facturation.service'
import {
  InvoiceGenerationItem,
  InvoiceGenerationResult,
} from '@/types/facturation.types'
import {
  formatCurrency,
  getBilledEntityTypeBadgeClasses,
  getBilledEntityTypeLabel,
} from '@/utils/facturation-utils'
import { InvoiceLinesTable } from '@/components/facturation/InvoiceDetailModal'
import { toastError, toastSuccess } from '@/utils/toast'

const STATUS_LABELS: Record<InvoiceGenerationItem['status'], string> = {
  created: 'Créée',
  would_create: 'Serait créée',
  already_exists: 'Déjà existante',
  skipped_no_flux: 'Ignorée',
  flux_failed: 'Échec Flux',
}

const STATUS_CLASSES: Record<InvoiceGenerationItem['status'], string> = {
  created: 'bg-green-50 text-green-800',
  would_create: 'bg-blue-50 text-blue-800',
  already_exists: 'bg-amber-50 text-amber-800',
  skipped_no_flux: 'bg-gray-100 text-gray-700',
  flux_failed: 'bg-red-50 text-red-800',
}

const STATUS_BORDER_CLASSES: Record<InvoiceGenerationItem['status'], string> = {
  created: 'border-l-green-500',
  would_create: 'border-l-blue-500',
  already_exists: 'border-l-amber-500',
  skipped_no_flux: 'border-l-gray-400',
  flux_failed: 'border-l-red-500',
}

function SimulationInvoiceCard({ item, index }: { item: InvoiceGenerationItem; index: number }) {
  return (
    <article
      className={`rounded-lg border border-[var(--color-border)] border-l-4 ${STATUS_BORDER_CLASSES[item.status]} bg-white shadow-sm overflow-hidden`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 bg-[var(--color-gray-50)] border-b border-[var(--color-border)]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Facture {index + 1}
            </span>
            <span
              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getBilledEntityTypeBadgeClasses(item.entityType)}`}
            >
              {getBilledEntityTypeLabel(item.entityType)}
            </span>
          </div>
          <h3 className="mt-1 text-base font-semibold text-[var(--color-text-primary)]">
            {item.entityName}
          </h3>
          {item.fluxId != null && (
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">Flux #{item.fluxId}</p>
          )}
          {item.reason && (
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">{item.reason}</p>
          )}
          {item.pdfUrl && (
            <a
              href={item.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-[var(--color-primary)] hover:underline mt-1"
            >
              Voir le PDF
            </a>
          )}
        </div>
        <span
          className={`shrink-0 inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASSES[item.status]}`}
        >
          {STATUS_LABELS[item.status]}
        </span>
      </header>

      {item.lines && item.lines.length > 0 && (
        <div className="p-4 border-b border-[var(--color-border)]">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)] mb-3">
            Lignes de facturation ({item.lines.length})
          </p>
          <InvoiceLinesTable lines={item.lines} />
        </div>
      )}

      <footer className="px-4 py-3 bg-[var(--color-gray-50)]">
        <div className="flex flex-wrap justify-end gap-6 text-sm">
          <span className="text-[var(--color-text-secondary)]">
            Licences : <strong>{item.licenseCount}</strong>
          </span>
          <span className="text-[var(--color-text-secondary)]">
            HT : <strong>{formatCurrency(item.totalHt)}</strong>
          </span>
          <span className="text-[var(--color-text-primary)] font-semibold">
            TTC : {formatCurrency(item.totalTtc)}
          </span>
        </div>
      </footer>
    </article>
  )
}

export const InvoiceSimulationSection: React.FC = () => {
  const now = new Date()
  const [periodYear, setPeriodYear] = useState(now.getFullYear())
  const [periodMonth, setPeriodMonth] = useState(now.getMonth() + 1)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<InvoiceGenerationResult | null>(null)

  const runSimulation = async () => {
    setIsLoading(true)
    try {
      const response = await facturationService.generateInvoices({
        periodYear,
        periodMonth,
        dryRun: true,
      })
      setResult(response)
      toastSuccess(
        `Simulation : ${response.createdCount} facture(s) seraient créées, ${response.skippedCount} ignorée(s)`
      )
    } catch (error) {
      toastError(
        error instanceof Error ? error.message : 'Erreur lors de la simulation'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Simulation de facturation
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Prévisualisez les factures qui seraient émises pour une campagne mensuelle donnée
          (équivalent au cron du 1er du mois), sans créer de document dans Flux.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 max-w-md">
          <Input
            label="Mois de facturation"
            type="number"
            min={1}
            max={12}
            value={periodMonth}
            onChange={e => setPeriodMonth(Number(e.target.value))}
          />
          <Input
            label="Année"
            type="number"
            min={2000}
            max={2100}
            value={periodYear}
            onChange={e => setPeriodYear(Number(e.target.value))}
          />
        </div>
        <Button className='py-[11px]' onClick={runSimulation} disabled={isLoading}>
          {isLoading ? 'Chargement...' : 'Lancer la simulation'}
        </Button>
      </div>

      {result && (
        <div className="rounded-lg border border-[var(--color-border)] overflow-hidden">
          <div className="bg-[var(--color-gray-50)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
            Période {String(result.periodMonth).padStart(2, '0')}/{result.periodYear}
            {result.dryRun ? ' — simulation' : ' — génération réelle'} —{' '}
            <strong>{result.createdCount}</strong> créée(s),{' '}
            <strong>{result.skippedCount}</strong> ignorée(s)
          </div>
          <div className="p-4 space-y-4">
            {result.items.map((item, index) => (
              <SimulationInvoiceCard
                key={item.invoiceId ?? `${item.entityName}-${item.entityType}-${index}`}
                item={item}
                index={index}
              />
            ))}
          </div>
          {result.skippedLicenses && result.skippedLicenses.length > 0 && (
            <div className="border-t border-[var(--color-border)] px-4 py-3 bg-amber-50/50">
              <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
                Licences ignorées ({result.skippedLicenses.length})
              </p>
              <ul className="text-sm text-[var(--color-text-secondary)] space-y-1">
                {result.skippedLicenses.map(entry => (
                  <li key={entry.clientId}>
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {entry.clientName}
                    </span>
                    {' — '}
                    {entry.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
